
import React, { useState, useMemo } from 'react';
import { Job, UserRole, LocalRequirement } from '../types';
import { MapPin, Calendar, DollarSign, Award, Briefcase, ChevronRight, Star, ShieldCheck, ExternalLink, Upload, ChevronDown, ChevronUp, Clock, AlertCircle } from 'lucide-react';
import { differenceInHours, parseISO, isBefore } from 'date-fns';

interface JobCardProps {
  job: Job;
  userRole: UserRole;
  onApply?: (jobId: string) => void;
  onViewDetails?: (jobId: string) => void;
  onUploadLicense?: () => void;
  isApplied?: boolean;
  employerRating?: number;
  companyLogo?: string;
}

export const JobCard: React.FC<JobCardProps> = ({ 
  job, 
  userRole, 
  onApply, 
  onViewDetails, 
  onUploadLicense,
  isApplied,
  employerRating = 4.8,
  companyLogo
}) => {
  const [showReqs, setShowReqs] = useState(false);

  const deadlineStatus = useMemo(() => {
    if (!job.deadline) return null;
    const deadlineDate = parseISO(job.deadline);
    const now = new Date();
    
    if (isBefore(deadlineDate, now)) return 'EXPIRED';
    
    const hoursLeft = differenceInHours(deadlineDate, now);
    if (hoursLeft <= 48) return 'CLOSING_SOON';
    
    return 'ACTIVE';
  }, [job.deadline]);

  return (
    <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-xl hover:border-emerald-200 transition-all p-6 flex flex-col h-full group relative overflow-hidden">
      {deadlineStatus === 'CLOSING_SOON' && (
        <div className="absolute top-0 right-0 left-0 bg-amber-500 text-white py-1 px-4 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 z-10 animate-pulse">
          <AlertCircle className="w-3 h-3" />
          Closing Soon: Only 48 Hours Left
        </div>
      )}
      
      <div className={`flex justify-between items-start mb-4 ${deadlineStatus === 'CLOSING_SOON' ? 'mt-4' : ''}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg ${
              job.status === 'OPEN' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400'
            }`}>
              {job.status}
            </span>
            {job.deadline && deadlineStatus === 'ACTIVE' && (
              <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Until {job.deadline}
              </span>
            )}
          </div>
          <h3 className="text-xl font-black text-slate-800 mt-3 truncate group-hover:text-emerald-700 transition-colors">{job.title}</h3>
          <div className="flex items-center mt-1">
            <p className="text-sm text-slate-500 font-bold truncate mr-2">{job.companyName}</p>
            <div className="flex items-center bg-amber-50 px-1.5 py-0.5 rounded-md">
              <Star className="w-3 h-3 text-amber-500 fill-amber-500 mr-1" />
              <span className="text-[10px] font-black text-amber-700">{employerRating}</span>
            </div>
          </div>
        </div>
        <div className="bg-slate-50 rounded-2xl group-hover:bg-emerald-50 transition-colors overflow-hidden flex items-center justify-center w-12 h-12 flex-shrink-0">
          {companyLogo ? (
            <img src={companyLogo} alt="Company Logo" className="w-full h-full object-cover" />
          ) : (
            <Briefcase className="w-6 h-6 text-slate-300 group-hover:text-emerald-600" />
          )}
        </div>
      </div>

      <div className="space-y-3 mb-6 flex-grow">
        <div className="flex items-center text-sm text-slate-500 font-medium">
          <Calendar className="w-4 h-4 mr-3 text-slate-300" />
          Scheduled: {job.date}
        </div>
        <div className="flex items-center text-sm text-slate-500 font-medium">
          <MapPin className="w-4 h-4 mr-3 text-slate-300" />
          {job.location}
        </div>
        <div className="flex items-center text-sm text-emerald-600 font-black">
          <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
          {job.payRate}
        </div>
      </div>

      {/* Local Requirements Intelligence */}
      {job.localRequirements && job.localRequirements.length > 0 && (
        <div className="mb-6 bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden">
          <button 
            onClick={() => setShowReqs(!showReqs)}
            className="w-full px-4 py-3 flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-100 transition-colors"
          >
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              Compliance Intel: {job.location}
            </span>
            {showReqs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {showReqs && (
            <div className="p-4 pt-0 space-y-3">
              {job.localRequirements.map((req, idx) => (
                <div key={idx} className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm space-y-2">
                  <div className="flex justify-between items-start">
                    <p className="text-[9px] font-black text-emerald-600 uppercase tracking-tight">{req.task}</p>
                  </div>
                  <p className="text-[11px] font-bold text-slate-700 leading-tight">{req.requirement}</p>
                  <p className="text-[9px] text-slate-400 font-medium">{req.authority}</p>
                  
                  <div className="flex gap-2 pt-1">
                    <a 
                      href={req.applyUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-[9px] font-black hover:bg-emerald-100 transition-colors"
                    >
                      <ExternalLink className="w-2.5 h-2.5" /> Get License
                    </a>
                    <button 
                      onClick={(e) => { e.stopPropagation(); onUploadLicense?.(); }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-[9px] font-black hover:bg-slate-200 transition-colors"
                    >
                      <Upload className="w-2.5 h-2.5" /> Upload Proof
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {job.requiredSkills.map(skill => (
            <span key={skill} className="text-[10px] bg-slate-50 text-slate-500 font-bold px-3 py-1.5 rounded-xl border border-slate-100">
              {skill}
            </span>
          ))}
          {job.requiredCerts.map(cert => (
            <span key={cert} className="text-[10px] bg-amber-50 text-amber-700 font-black px-3 py-1.5 rounded-xl border border-amber-100 flex items-center">
              <Award className="w-3 h-3 mr-1" />
              {cert}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-auto flex gap-3">
        <button 
          onClick={() => onViewDetails?.(job.id)}
          className="flex-1 px-4 py-3 text-sm font-bold text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-all"
        >
          Details
        </button>
        {userRole === UserRole.WORKER && (
          <button 
            disabled={isApplied || job.status !== 'OPEN' || deadlineStatus === 'EXPIRED'}
            onClick={() => onApply?.(job.id)}
            className={`flex-[1.5] px-4 py-3 text-sm font-black rounded-2xl transition-all flex items-center justify-center ${
              isApplied 
                ? 'bg-emerald-100 text-emerald-700 cursor-default' 
                : deadlineStatus === 'EXPIRED'
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700 active:scale-95 shadow-lg shadow-emerald-200'
            }`}
          >
            {isApplied ? 'Applied' : deadlineStatus === 'EXPIRED' ? 'Closed' : 'Quick Apply'}
            {!isApplied && deadlineStatus !== 'EXPIRED' && <ChevronRight className="w-4 h-4 ml-1" />}
          </button>
        )}
      </div>
    </div>
  );
};
